<template>
  <BaseModal
    :show="show"
    modal-id="payment-breakdown-modal"
    size="md"
    custom-class="custom-modal-payment"
    @close="$emit('close')"
  >
    <template #header>
      <div class="custom-header w-100">
        <h4 class="modal-title text-white">DESGLOSE DEL MONTO</h4>
      </div>
    </template>

    <template #default>
      <div class="p-3" v-if="offer">
        <!-- Título de la ubicación de la oferta -->
        <div class="location-banner mb-4">
          <iconify-icon icon="lucide:map-pin" class="location-icon" />
          <span class="location-text">{{ offer.cityName }}, {{ offer.stateName }}</span>
        </div>

        <!-- Inversión total destacado -->
        <div class="amount-box total-investment-box mb-4">
          <div class="amount-label d-flex align-items-center">
            <iconify-icon icon="lucide:award" class="me-2 text-warning-custom" />
            <span class="fw-bold">Inversión Total del Curso</span>
          </div>
          <span class="amount-value total-value">$ {{ offer.amountFormatted }}</span>
        </div>

        <div class="breakdown-sections">
          <!-- Bloque Inscripción / Matrícula -->
          <div v-if="offer.check_enrollment === 1" class="breakdown-card mb-3">
            <div class="breakdown-header d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
              <div class="d-flex align-items-center text-primary-custom">
                <iconify-icon icon="lucide:file-text" class="me-2" />
                <span class="fw-semibold">Matrícula (Inscripción)</span>
              </div>
              <span class="badge bg-primary-custom text-white">Requerido</span>
            </div>
            <div class="d-flex justify-content-between align-items-center py-1">
              <span class="text-muted">Monto de inscripción:</span>
              <span class="fw-bold text-dark">$ {{ offer.enrollment_amount_formatted }}</span>
            </div>
          </div>

          <!-- Bloque Cuotas -->
          <div v-if="offer.check_installments === 1 && offer.installments && offer.installments.length > 0" class="breakdown-card">
            <div class="breakdown-header d-flex justify-content-between align-items-center pb-2 mb-2 border-bottom">
              <div class="d-flex align-items-center text-info-custom">
                <iconify-icon icon="lucide:calendar" class="me-2" />
                <span class="fw-semibold">Financiamiento (Cuotas)</span>
              </div>
              <span class="badge bg-info-custom text-white">{{ offer.installments.length }} {{ offer.installments.length === 1 ? 'Cuota' : 'Cuotas' }}</span>
            </div>
            <div class="installments-list">
              <div
                v-for="(inst, index) in offer.installments"
                :key="inst.id"
                class="installment-item d-flex justify-content-between align-items-center py-2"
                :class="{ 'border-bottom-dashed': index < offer.installments.length - 1 }"
              >
                <div class="d-flex flex-column">
                  <span class="fw-medium text-dark">Cuota {{ index + 1 }}</span>
                  <span class="text-muted small">
                    Vence: {{ formatDate(inst.due_date) }}
                  </span>
                </div>
                <span class="fw-bold text-dark">$ {{ inst.amountFormatted }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <button class="btn btn-secondary rounded-pill px-4" @click.prevent="$emit('close')">
        Entendido
      </button>
    </template>
  </BaseModal>
</template>

<script setup>
import moment from 'moment';
import BaseModal from '@/components/common/BaseModal.vue';

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  offer: {
    type: Object,
    default: null,
  },
});

defineEmits(['close']);

const formatDate = (date) => {
  if (!date) return '';
  return moment(date).format('DD/MM/YYYY');
};
</script>

<style scoped>
.custom-header {
  padding: 0;
}

.custom-close {
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  color: #fff;
  font-size: 2rem;
  background: none;
  border: none;
  opacity: 1;
  cursor: pointer;
  z-index: 10;
}

.location-banner {
  background: rgba(109, 38, 58, 0.05);
  border-radius: 8px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  color: #6d263a;
  border: 1px solid rgba(109, 38, 58, 0.1);
}

.location-icon {
  font-size: 1.2rem;
  margin-right: 8px;
}

.location-text {
  font-weight: 600;
  font-size: 0.95rem;
}

.amount-box {
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-investment-box {
  background: linear-gradient(135deg, #6d263a 60%, #a14e5a 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(109, 38, 58, 0.15);
}

.amount-label {
  font-size: 0.95rem;
}

.amount-value {
  font-size: 1.35rem;
  font-weight: 700;
}

.total-value {
  color: #b49b57;
}

.text-warning-custom {
  color: #b49b57;
  font-size: 1.2rem;
}

.breakdown-card {
  background: #fdfdfd;
  border: 1px solid #eef2f5;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.text-primary-custom {
  color: #6d263a;
}

.bg-primary-custom {
  background-color: #6d263a;
}

.text-info-custom {
  color: #2b7a78;
}

.bg-info-custom {
  background-color: #2b7a78;
}

.border-bottom {
  border-bottom: 1px solid #f1f4f6 !important;
}

.border-bottom-dashed {
  border-bottom: 1px dashed #eef2f5;
}

.installment-item {
  transition: background-color 0.2s;
}

.installment-item:hover {
  background-color: rgba(0, 0, 0, 0.01);
}

.small {
  font-size: 0.8rem;
}
</style>
